/* eslint-disable no-debugger */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import prepareBreadcrumbStructuredPageData from "@insite/client-framework/Common/Utilities/prepareBreadcrumbStructuredPageData";
import prepareOrganizationStructuredPageData from "@insite/client-framework/Common/Utilities/prepareOrganizationStructuredPageData";
import { getUrl } from "@insite/client-framework/ServerSideRendering";
import { Website } from "@insite/client-framework/Services/WebsiteService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedBrandPath, getSelectedProductPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBrandStateByPath } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getById, getDataView } from "@insite/client-framework/Store/Data/DataState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getComputedVariantProduct, getProductState, getProductStateByPath } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { generateLinksFrom } from "@tLinc/Overrides/Components/PageBreadcrumbs";
import * as React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const { pathname } = location;
    const currentPageData = getCurrentPage(state);
    let productPath =
        getSelectedProductPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    let queryString = location.search;
    if (location.pathname.toLowerCase().startsWith("/content/page/") && productPath.indexOf("?") > -1) {
        queryString = productPath ? productPath.substring(productPath.indexOf("?")) : "";
        productPath = productPath.substring(0, productPath.indexOf("?"));
    }
    const catalogPageState = getCatalogPageStateByPath(state, productPath);
    const productState = getProductStateByPath(state, productPath);
    const brandPath = getSelectedBrandPath(state) || pathname;
    const brandState = getBrandStateByPath(state, brandPath);

    const catalogOgImage = catalogPageState.value?.openGraphImage || catalogPageState.value?.primaryImagePath;
    const productdetailOgImage =
        productState.value?.mediumImagePath || productState.value?.largeImagePath || productState.value?.smallImagePath;
    const brandOgImage = brandState.value?.logoSmallImagePath || brandState.value?.logoLargeImagePath;
    const variantProductState = getProductState(state, state.pages.productDetails.selectedProductId);
    const computedVariantProduct = getComputedVariantProduct(productState, variantProductState);
    return {
        breadcrumbLinks: state.components.breadcrumbs.links,
        linksState: state.links,
        nodeId: getCurrentPage(state).nodeId,
        homePageUrl: getHomePageUrl(state),
        enableStructuredData:
            state.context?.settings?.settingsCollection?.websiteSettings?.enableOrganizationStructuredData,
        website: state.context?.website,
        catalogOgImage,
        productdetailOgImage,
        brandOgImage,
        location,
        currentPageData,
        productState: computedVariantProduct,
        websiteName: state.context.website.name,
    };
};

type Props = ReturnType<typeof mapStateToProps>;

const CustomSchema: React.FC<Props> = (props: Props) => {
    const {
        breadcrumbLinks,
        linksState,
        location,
        enableStructuredData,
        website,
        nodeId,
        homePageUrl,
        catalogOgImage,
        productdetailOgImage,
        brandOgImage,
        currentPageData,
        productState: { value: product },
        websiteName,
    } = props;
    const { fields } = currentPageData;
    const links = breadcrumbLinks || generateLinksFrom(linksState, nodeId, homePageUrl);
    const windowsOrigin = window?.location?.origin;
    const pathname = location.pathname;
    const isAboutPage = pathname.includes("/about-linc");
    const isBlogPage = pathname.includes("/blog");
    const isContactPage = pathname.includes("/contact");
    const href = window?.location?.href;
    const isHomepage = currentPageData?.type === 'HomePage';
    const isBreadCrumbPresent = document.getElementById('breadcrumb-structured-data');
    const isProductDetailPage = currentPageData?.type === 'ProductDetailsPage';

    const prepareStructuredPageData = (website: Website, href: string) => {
        const url: { protocol: string; host: string } = IS_SERVER_SIDE ? getUrl()! : window.location;
        const authority = `${url.protocol}//${url.host}`;
        const data = {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: website.companyName,
            description: website.description,
            address: {
                "@type": "PostalAddress",
                streetAddress: [website.address1, website.address2].filter(o => o).join(", "),
                addressLocality: website.city,
                addressRegion: website.state,
                postalCode: website.postalCode,
                addressCountry: website.companyCountry,
            },
            telephone: website.companyPhone,
            url: href,
            logo:
                website.companyLogoImagePath &&
                (website.companyLogoImagePath.indexOf("http") === 0
                    ? website.companyLogoImagePath
                    : authority + website.companyLogoImagePath),
            sameAs: website.companySocialAccounts ? website.companySocialAccounts.split(",") : [],
        } as any;
        return JSON.stringify(data);
    };
    const structuredData =
        (isAboutPage || isContactPage) && !isBlogPage && enableStructuredData && website
            ? prepareStructuredPageData(website, href) // Ensure this function returns valid JSON-LD data
            : null;
    const breadCrumbsStructures =
        !isHomepage && !isProductDetailPage && !isBreadCrumbPresent && enableStructuredData && website ? prepareBreadcrumbStructuredPageData(links) : null;

    const removeExistingScripts = () => {
        const existingScripts = [
            document.getElementById("about-structured-data"),
            document.getElementById("breadcrumbAbout-structured-data"),
        ];
        existingScripts.forEach(script => script && document.head.removeChild(script));
    };

    const appendScript = (id: string, data: string) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.id = id;
        script.innerHTML = data;
        document.head.appendChild(script);
    };

    const setMetaTag = (name: string, content: string) => {
        let tag = document.querySelector(
            name.includes("twitter") ? `meta[name='${name}']` : `meta[property='${name}']`,
        );
        if (!tag) {
            tag = document.createElement("meta");
            name.includes("twitter") ? tag.setAttribute("name", name) : tag.setAttribute("property", name);
            document.head.appendChild(tag);
        }
        tag.setAttribute("content", content);
    };

    useEffect(() => {
        // Setting Twitter meta tags dynamically
        const title = document.title;
        let description = document.querySelector('meta[name="description"]');
        const ogImage = document.querySelector('meta[property="og:image"]');
        const productDetailElement = document.querySelector(
            'h1[data-test-selector="productDetails_productDescription"]',
        );
        const brandPageElement = document.querySelector('a[data-test-selector="brandToProductPageLink"]');
        const imgUrl = productDetailElement
            ? productdetailOgImage || `${windowsOrigin}/userfiles/images/linc-logo.png`
            : brandPageElement
            ? brandOgImage || `${windowsOrigin}/userfiles/images/linc-logo.png`
            : ogImage?.getAttribute("content") ||
              catalogOgImage ||
              productdetailOgImage ||
              `${windowsOrigin}/userfiles/images/linc-logo.png`;
        const extension = getImageExtension(imgUrl);
        const productDetailTitle = (isProductDetailPage && product) ? `Buy ${product.productTitle} Online | ${websiteName}` : '';

        function getImageExtension(url: string) {
            // Remove everything after the '?' (query params)
            url = url.split("?")[0];

            // Extract the extension using a regular expression
            const regex = /(?:\.([^.]+))?$/;
            const matches = url.match(regex);

            return matches && matches[1] ? matches[1] : null;
        }

        // Update document.title if fields?.overrideSeoTitle is present
        const metaTitle = productDetailTitle || fields?.overrideSeoTitle || title;
        if (fields?.overrideSeoTitle) {
            document.title = metaTitle; // Update the page title
        }

        if(isProductDetailPage && product && links.length > 0){
            document.title = productDetailTitle;
            const productType = links && links[links.length - 2].children;
            if (!description) {
                description = document.createElement('meta');
                description.setAttribute('name', 'description');
                document.head.appendChild(description);
            }
            description?.setAttribute('content', `Order ${product.productTitle} at Linc Systems. Premium quality ${productType} available with fast shipping. Shop Now!`);
        }

        setMetaTag("twitter:card", "Linc Systems");
        setMetaTag("twitter:site", "Linc Systems");
        setMetaTag("twitter:title", metaTitle);
        setMetaTag("twitter:description", description?.getAttribute("content") || "");
        setMetaTag("twitter:image", imgUrl);
        setMetaTag("og:image", imgUrl);

        setMetaTag("og:description", description?.getAttribute("content") || "");
        setMetaTag("og:locale", "en-us");
        setMetaTag("og:type", "website");
        setMetaTag("og:title", metaTitle);
        setMetaTag("og:url", href);
        setMetaTag("og:site_name", "Linc Systems");
        setMetaTag("og:image:type", extension || "webp");
        setMetaTag("og:image:type:width", "1200");
        setMetaTag("og:image:type:height", "550");

        // Clean up function to remove meta tags when component is unmounted
        return () => {
            document
                .querySelectorAll("meta[name^='twitter:'], meta[property^='og:']")
                .forEach(tag => document.head.removeChild(tag));
        };
    }, [location, catalogOgImage, productdetailOgImage, brandOgImage, breadcrumbLinks]);

    useEffect(() => {
        removeExistingScripts();

        // If structuredData is available, create and append the new script
        if (structuredData?.trim()) {
            appendScript("about-structured-data", structuredData);
        }
        if (breadCrumbsStructures?.trim()) {
            appendScript("breadcrumbAbout-structured-data", breadCrumbsStructures);
        }

        // Cleanup function to remove the script when the component unmounts or data changes
        return () => {
            removeExistingScripts();
        };
    }, [structuredData, breadCrumbsStructures, website]);

    useEffect(() => {
        const existingEmptyScript = document.querySelector('script[type="application/ld+json"]');
        if (existingEmptyScript?.innerHTML?.trim() === "") {
            document.head.removeChild(existingEmptyScript);
        }
        const languageRegion = "en-us";
        // Create the <link> element
        const linkTag = document.createElement("link");
        linkTag.rel = "alternate";
        linkTag.hreflang = languageRegion;
        linkTag.href = href;

        // Append the link tag to the <head>
        document.head.appendChild(linkTag);
        return () => {
            document.head.removeChild(linkTag);
        };
    }, [location, href]);

    return null;
};

const definition: WidgetDefinition = {
    group: "Basic",
    displayName: "Custom Schema",
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(CustomSchema),
    definition,
};

export default widgetModule;
